/**
 * ProviderWeeklyScheduleModal
 * 3-step modal for configuring per-weekday schedules for clinics using "Hora Marcada".
 *
 * Step 1: Select active days
 * Step 2: Set default times + slot configuration for all selected days
 * Step 3: Adjust individual days if needed
 *
 * O que a pessoa muda no passo 2 vale para todos os dias marcados, campo a campo: só o que ela
 * mexeu naquela abertura do assistente sobrescreve; o resto fica como está em cada dia.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import WeekDaySelector from '../../professional-schedules/components/WeekDaySelector';
import { WeekDay, WEEK_DAY_NAMES } from '../../../types/professionalSchedule';
import { ProviderWeeklySchedule } from '../../../types/provider';
import { providerService } from '../../../services/providerService';
import { mensagemDeFalha } from '../../../utils/acessoNegado';
import {
  classificarBlocosDoDia,
  capacidadeParaCampo,
  campoParaCapacidade,
  periodoPelaMetade,
} from './classificarBlocosDoDia';

interface DayConfig {
  weekDay: number;
  morningStart: string;
  morningEnd: string;
  morningCapacity: string;
  afternoonStart: string;
  afternoonEnd: string;
  afternoonCapacity: string;
}

interface DefaultTimes {
  morningStart: string;
  morningEnd: string;
  afternoonStart: string;
  afternoonEnd: string;
  capacity: string;
}

type CampoPadrao = keyof DefaultTimes;

export interface SlotConfig {
  defaultSlotDuration?: number;
  concurrentCapacity?: number | null;
}

interface ProviderWeeklyScheduleModalProps {
  open: boolean;
  onClose: (slotConfig?: SlotConfig) => void;
  providerId: string | number;
  providerName?: string;
  defaultMorningStart?: string;
  defaultMorningEnd?: string;
  defaultAfternoonStart?: string;
  defaultAfternoonEnd?: string;
}

const STEPS = ['Dias Ativos', 'Horário Padrão', 'Ajuste por Dia'];

const DURACAO_PADRAO = 30;

const dayConfigFromDefaults = (weekDay: number, padrao: DefaultTimes): DayConfig => ({
  weekDay,
  morningStart: padrao.morningStart,
  morningEnd: padrao.morningEnd,
  morningCapacity: padrao.capacity,
  afternoonStart: padrao.afternoonStart,
  afternoonEnd: padrao.afternoonEnd,
  afternoonCapacity: padrao.capacity,
});

// Os períodos que o dia tem, separados por barra; dia só da tarde mostra só a tarde
const resumoDoDia = (config: DayConfig): string =>
  [
    config.morningStart ? `${config.morningStart}-${config.morningEnd}` : '',
    config.afternoonStart ? `${config.afternoonStart}-${config.afternoonEnd}` : '',
  ]
    .filter(Boolean)
    .join(' / ');

const timeFieldProps = { inputLabel: { shrink: true } };

const ProviderWeeklyScheduleModal: React.FC<ProviderWeeklyScheduleModalProps> = ({
  open,
  onClose,
  providerId,
  providerName,
  defaultMorningStart = '',
  defaultMorningEnd = '',
  defaultAfternoonStart = '',
  defaultAfternoonEnd = '',
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDays, setSelectedDays] = useState<WeekDay[]>([]);
  const [defaultTimes, setDefaultTimes] = useState<DefaultTimes>({
    morningStart: defaultMorningStart,
    morningEnd: defaultMorningEnd,
    afternoonStart: defaultAfternoonStart,
    afternoonEnd: defaultAfternoonEnd,
    capacity: '',
  });
  const [slotDuration, setSlotDuration] = useState<number>(DURACAO_PADRAO);
  const [concurrentCapacity, setConcurrentCapacity] = useState<string>('');
  // Guarda também os dias desmarcados no passo 1, até o assistente fechar; só os marcados são salvos.
  const [dayConfigs, setDayConfigs] = useState<DayConfig[]>([]);
  const [mexidos, setMexidos] = useState<CampoPadrao[]>([]);
  const [diasAlterados, setDiasAlterados] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  // Duração e simultâneos vêm só do servidor: sem a leitura, avançar ou salvar os sobrescreveria
  const [carregou, setCarregou] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Os horários sugeridos só servem a quem ainda não tem grade; ficam fora das dependências
  // da carga para que uma mudança deles não reinicie o passo em que a pessoa está.
  const sugestoes = useRef({
    morningStart: defaultMorningStart,
    morningEnd: defaultMorningEnd,
    afternoonStart: defaultAfternoonStart,
    afternoonEnd: defaultAfternoonEnd,
  });
  sugestoes.current = {
    morningStart: defaultMorningStart,
    morningEnd: defaultMorningEnd,
    afternoonStart: defaultAfternoonStart,
    afternoonEnd: defaultAfternoonEnd,
  };

  // Load existing schedule and slot config when modal opens
  useEffect(() => {
    if (!open || !providerId) return;

    // Resposta de um prestador que já não é o aberto (ou de uma abertura anterior) é ignorada.
    let obsoleta = false;

    const loadSchedule = async () => {
      setLoading(true);
      setCarregou(false);
      setError(null);
      setMexidos([]);
      setDiasAlterados([]);
      setSelectedDays([]);
      setDayConfigs([]);
      setDefaultTimes({ ...sugestoes.current, capacity: '' });
      setSlotDuration(DURACAO_PADRAO);
      setConcurrentCapacity('');
      try {
        const { schedules, config } = await providerService.getWeeklyScheduleCompleto(providerId);
        if (obsoleta) return;

        const days = [...new Set(schedules.map(s => s.weekDay))].sort((a, b) => a - b) as WeekDay[];
        const configs: DayConfig[] = days.map(day => {
          const { manha, tarde } = classificarBlocosDoDia(schedules.filter(s => s.weekDay === day));
          return {
            weekDay: day,
            morningStart: manha?.startTime || '',
            morningEnd: manha?.endTime || '',
            morningCapacity: capacidadeParaCampo(manha?.capacity),
            afternoonStart: tarde?.startTime || '',
            afternoonEnd: tarde?.endTime || '',
            afternoonCapacity: capacidadeParaCampo(tarde?.capacity),
          };
        });
        setSelectedDays(days);
        setDayConfigs(configs);

        const primeiro = configs[0];
        setDefaultTimes(
          primeiro
            ? {
                morningStart: primeiro.morningStart,
                morningEnd: primeiro.morningEnd,
                afternoonStart: primeiro.afternoonStart,
                afternoonEnd: primeiro.afternoonEnd,
                capacity: '',
              }
            : { ...sugestoes.current, capacity: '' }
        );
        setSlotDuration(config.defaultSlotDuration ?? DURACAO_PADRAO);
        setConcurrentCapacity(capacidadeParaCampo(config.concurrentCapacity));
        setCarregou(true);
      } catch (err) {
        if (!obsoleta) setError(mensagemDeFalha(err, 'Erro ao carregar horários existentes'));
      } finally {
        if (!obsoleta) setLoading(false);
      }
    };

    loadSchedule();
    return () => {
      obsoleta = true;
    };
  }, [open, providerId]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setError(null);
    }
  }, [open]);

  const handleNext = useCallback(() => {
    if (activeStep === 0) {
      // Dia acrescentado agora recebe todos os valores atuais do passo 2
      setDayConfigs(prev => {
        const existentes = new Set(prev.map(d => d.weekDay));
        const novos = selectedDays
          .filter(day => !existentes.has(day))
          .map(day => dayConfigFromDefaults(day, defaultTimes));
        return [...prev, ...novos];
      });
    }
    if (activeStep === 1 && mexidos.length > 0) {
      // O que foi mexido no passo 2 sobrescreve os dias marcados; o sinal desliga ao aplicar
      setDayConfigs(prev =>
        prev.map(config => {
          if (!selectedDays.includes(config.weekDay as WeekDay)) return config;
          const atualizado = { ...config };
          if (mexidos.includes('morningStart')) atualizado.morningStart = defaultTimes.morningStart;
          if (mexidos.includes('morningEnd')) atualizado.morningEnd = defaultTimes.morningEnd;
          if (mexidos.includes('afternoonStart')) atualizado.afternoonStart = defaultTimes.afternoonStart;
          if (mexidos.includes('afternoonEnd')) atualizado.afternoonEnd = defaultTimes.afternoonEnd;
          if (mexidos.includes('capacity')) {
            atualizado.morningCapacity = defaultTimes.capacity;
            atualizado.afternoonCapacity = defaultTimes.capacity;
          }
          return atualizado;
        })
      );
      setDiasAlterados(selectedDays);
      setMexidos([]);
    }
    setActiveStep(prev => prev + 1);
  }, [activeStep, selectedDays, defaultTimes, mexidos]);

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleDefaultChange = (field: CampoPadrao, value: string) => {
    setDefaultTimes(prev => ({ ...prev, [field]: value }));
    setMexidos(prev => (prev.includes(field) ? prev : [...prev, field]));
  };

  const handleDayConfigChange = (weekDay: number, field: keyof DayConfig, value: string) => {
    setDayConfigs(prev =>
      prev.map(d => (d.weekDay === weekDay ? { ...d, [field]: value } : d))
    );
  };

  const diasMarcados = dayConfigs
    .filter(d => selectedDays.includes(d.weekDay as WeekDay))
    .sort((a, b) => a.weekDay - b.weekDay);

  const handleSave = async () => {
    setError(null);

    for (const config of diasMarcados) {
      const recusa = periodoPelaMetade(WEEK_DAY_NAMES[config.weekDay as WeekDay], config);
      if (recusa) {
        setError(recusa);
        return;
      }
    }

    setSaving(true);
    try {
      // Convert dayConfigs to API format (each period becomes a separate entry)
      const entries: Omit<ProviderWeeklySchedule, 'id' | 'providerId' | 'isActive'>[] = [];
      for (const config of diasMarcados) {
        if (config.morningStart && config.morningEnd) {
          entries.push({
            weekDay: config.weekDay,
            startTime: config.morningStart,
            endTime: config.morningEnd,
            capacity: campoParaCapacidade(config.morningCapacity),
          });
        }
        if (config.afternoonStart && config.afternoonEnd) {
          entries.push({
            weekDay: config.weekDay,
            startTime: config.afternoonStart,
            endTime: config.afternoonEnd,
            capacity: campoParaCapacidade(config.afternoonCapacity),
          });
        }
      }

      const slotConfig = {
        defaultSlotDuration: slotDuration,
        concurrentCapacity: campoParaCapacidade(concurrentCapacity),
      };
      await providerService.saveWeeklySchedule(providerId, entries, slotConfig);

      // Pass slot config back to parent form so it stays in sync (vazio incluído)
      onClose(slotConfig);
    } catch (err) {
      setError(mensagemDeFalha(err, 'Erro ao salvar horários'));
    } finally {
      setSaving(false);
    }
  };

  const canAdvance = () => {
    if (!carregou) return false;
    if (activeStep === 0) return selectedDays.length > 0;
    if (activeStep === 1) {
      if (periodoPelaMetade('', defaultTimes)) return false;
      return Boolean(
        (defaultTimes.morningStart && defaultTimes.morningEnd) ||
          (defaultTimes.afternoonStart && defaultTimes.afternoonEnd)
      );
    }
    return true;
  };

  const textoDoAviso =
    selectedDays.length === 1 ? 'o dia selecionado' : `todos os ${selectedDays.length} dias selecionados`;

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        Horários por Dia da Semana
        {providerName && (
          <Typography variant="body2" color="text.secondary">
            {providerName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !carregou ? (
          error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
              {STEPS.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Step 1: Select days */}
            {activeStep === 0 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Selecione os dias em que a clínica atende:
                </Typography>
                <WeekDaySelector
                  selectedDays={selectedDays}
                  onChange={setSelectedDays}
                />
              </Box>
            )}

            {/* Step 2: Default times + slot config */}
            {activeStep === 1 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Defina os horários padrão para todos os dias selecionados:
                </Typography>
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  O que você mudar aqui vale para <b>{textoDoAviso}</b>. O que você não mexer continua como está em cada dia.
                </Alert>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {/* Morning & Afternoon side by side */}
                  <Grid size={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Manhã
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Início"
                        type="time"
                        value={defaultTimes.morningStart}
                        onChange={e => handleDefaultChange('morningStart', e.target.value)}
                        fullWidth
                        size="small"
                        slotProps={timeFieldProps}
                      />
                      <TextField
                        label="Fim"
                        type="time"
                        value={defaultTimes.morningEnd}
                        onChange={e => handleDefaultChange('morningEnd', e.target.value)}
                        fullWidth
                        size="small"
                        slotProps={timeFieldProps}
                      />
                    </Box>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Tarde
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Início"
                        type="time"
                        value={defaultTimes.afternoonStart}
                        onChange={e => handleDefaultChange('afternoonStart', e.target.value)}
                        fullWidth
                        size="small"
                        slotProps={timeFieldProps}
                      />
                      <TextField
                        label="Fim"
                        type="time"
                        value={defaultTimes.afternoonEnd}
                        onChange={e => handleDefaultChange('afternoonEnd', e.target.value)}
                        fullWidth
                        size="small"
                        slotProps={timeFieldProps}
                      />
                    </Box>
                  </Grid>

                  {/* Divider */}
                  <Grid size={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  {/* Slot configuration */}
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Configuração de Atendimento
                    </Typography>
                  </Grid>
                  <Grid size={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Duração</InputLabel>
                      <Select
                        value={slotDuration}
                        label="Duração"
                        onChange={e => setSlotDuration(Number(e.target.value))}
                      >
                        <MenuItem value={15}>15 min</MenuItem>
                        <MenuItem value={20}>20 min</MenuItem>
                        <MenuItem value={25}>25 min</MenuItem>
                        <MenuItem value={30}>30 min</MenuItem>
                        <MenuItem value={40}>40 min</MenuItem>
                        <MenuItem value={45}>45 min</MenuItem>
                        <MenuItem value={60}>60 min</MenuItem>
                        <MenuItem value={90}>90 min</MenuItem>
                        <MenuItem value={120}>120 min</MenuItem>
                      </Select>
                      <FormHelperText>Tempo de cada atendimento</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      label="Simultâneos"
                      value={concurrentCapacity}
                      onChange={e => setConcurrentCapacity(e.target.value)}
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="3"
                      helperText="Atendimentos ao mesmo tempo"
                    />
                  </Grid>
                  <Grid size={4}>
                    <TextField
                      label="Simultâneos nos dias"
                      value={defaultTimes.capacity}
                      onChange={e => handleDefaultChange('capacity', e.target.value)}
                      fullWidth
                      type="number"
                      size="small"
                      placeholder="Padrão"
                      helperText="Opcional. Troca o valor de todos os dias"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 3: Per-day adjustment */}
            {activeStep === 2 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Ajuste individualmente se necessário:
                </Typography>
                {diasMarcados.map(config => (
                  <Accordion key={config.weekDay} defaultExpanded={diasMarcados.length <= 3}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">
                        {WEEK_DAY_NAMES[config.weekDay as WeekDay]}
                      </Typography>
                      {resumoDoDia(config) && (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {resumoDoDia(config)}
                        </Typography>
                      )}
                      {diasAlterados.includes(config.weekDay) && (
                        <Chip label="horário padrão alterado" size="small" sx={{ ml: 2 }} />
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2} alignItems="flex-end">
                        <Grid size={6}>
                          <Typography variant="caption" color="text.secondary">Manhã</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <TextField
                              label="Início"
                              type="time"
                              value={config.morningStart}
                              onChange={e => handleDayConfigChange(config.weekDay, 'morningStart', e.target.value)}
                              size="small"
                              slotProps={timeFieldProps}
                            />
                            <TextField
                              label="Fim"
                              type="time"
                              value={config.morningEnd}
                              onChange={e => handleDayConfigChange(config.weekDay, 'morningEnd', e.target.value)}
                              size="small"
                              slotProps={timeFieldProps}
                            />
                            <TextField
                              label="Simultâneos"
                              type="number"
                              value={config.morningCapacity}
                              onChange={e => handleDayConfigChange(config.weekDay, 'morningCapacity', e.target.value)}
                              size="small"
                              placeholder="—"
                            />
                          </Box>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="caption" color="text.secondary">Tarde</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <TextField
                              label="Início"
                              type="time"
                              value={config.afternoonStart}
                              onChange={e => handleDayConfigChange(config.weekDay, 'afternoonStart', e.target.value)}
                              size="small"
                              slotProps={timeFieldProps}
                            />
                            <TextField
                              label="Fim"
                              type="time"
                              value={config.afternoonEnd}
                              onChange={e => handleDayConfigChange(config.weekDay, 'afternoonEnd', e.target.value)}
                              size="small"
                              slotProps={timeFieldProps}
                            />
                            <TextField
                              label="Simultâneos"
                              type="number"
                              value={config.afternoonCapacity}
                              onChange={e => handleDayConfigChange(config.weekDay, 'afternoonCapacity', e.target.value)}
                              size="small"
                              placeholder="—"
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={saving}>
          Cancelar
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<BackIcon />} disabled={saving}>
            Voltar
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            endIcon={<NextIcon />}
            variant="contained"
            disabled={!canAdvance()}
          >
            Avançar
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            variant="contained"
            disabled={saving || !carregou}
          >
            Salvar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProviderWeeklyScheduleModal;
